import { List, useHandleObjList } from "hooks/useHandleObjList"
import {
  initialPricingVariablesItem,
  initialPurchasePlanForm,
  initialPurchasePlanPricing
} from "pages/purchase-plans/constants/initialStates"
import { useCallback, useEffect, useMemo, useState } from "react"
import { PricingUnits, PricingUnitsLabel } from "../constants/defaultValues"

export interface IDevModePricingVariables {
  list: List<App.Pricings>
  mathExp: string
  isValid: boolean
}

const usePurchasePlan = () => {
  // Purchase Plan Form

  const [purchasePlanForm, setPurchasePlanForm] = useState<App.IPurchasePlanForm>(initialPurchasePlanForm)

  const handlePurchasePlanFormChange = useCallback((key: keyof App.IPurchasePlanForm, value: string | boolean) => {
    setPurchasePlanForm((prevState) => ({
      ...prevState,
      [key]: value
    }))
  }, [])

  const isValidPurchasePlanForm = useMemo(() => {
    return (
      purchasePlanForm.name.length > 0 &&
      purchasePlanForm.planKey.length > 0 &&
      purchasePlanForm.description.length > 0 &&
      purchasePlanForm.description.length <= 250 &&
      purchasePlanForm.product.length > 0
    )
  }, [purchasePlanForm])

  // Benefits

  const {
    list: benefitsList,
    addElementAtBeginning: addBenefit,
    removeElement: removeBenefit,
    changeElement: changeBenefit,
    replaceList: replaceBenefitsList
  } = useHandleObjList<App.IBenefitItem>({ value: "" })

  const isValidBenefits = useMemo(() => {
    return benefitsList.every((benefit) => benefit.value.length > 0 && benefit.value.length <= 173)
  }, [benefitsList])

  // Simple Pricing Variables

  const [purchasePlanPricing, setPurchasePlanPricing] = useState<App.IPurchasePlanPricing>(initialPurchasePlanPricing)

  const [pricingUnit, setPricingUnit] = useState<PricingUnits | undefined>()

  const handlePricingChange = useCallback((key: keyof App.IPurchasePlanPricing, value: string | boolean) => {
    setPurchasePlanPricing((prevState) => ({
      ...prevState,
      [key]: value
    }))
  }, [])

  const handleFromPriceUnitSimplified = useCallback(() => {
    if (purchasePlanPricing.isSimplifiedPricing && pricingUnit === PricingUnits.CONSUMPTION) {
      const value = purchasePlanForm.isEnterprise ? PricingUnitsLabel.MONTHLY : PricingUnitsLabel.CONSUMPTION

      handlePricingChange("fromPriceUnit", value)
    }
  }, [purchasePlanForm.isEnterprise, purchasePlanPricing.isSimplifiedPricing, pricingUnit])

  useEffect(() => {
    handleFromPriceUnitSimplified()
  }, [handleFromPriceUnitSimplified, purchasePlanPricing.fromPriceUnit])

  const {
    list: pricingVariablesList,
    addElement: addPricingVariable,
    removeElement: removePricingVariable,
    changeElement: changePricingVariable,
    resetList,
    replaceList: replacePricingVariablesList
  } = useHandleObjList<App.ISimplePricingVariable>(initialPricingVariablesItem)

  const isValidSimplePricing = useMemo(() => {
    return (
      pricingUnit === PricingUnits.CONSUMPTION &&
      pricingVariablesList.every((pricingVariables) => pricingVariables.eventType?.length > 0) &&
      purchasePlanPricing.fromPriceUnit.length > 0 &&
      pricingVariablesList.length > 0
    )
  }, [pricingVariablesList, purchasePlanPricing.fromPriceUnit, pricingUnit])

  // Dev mode Pricing Variables

  const isDevMode = !purchasePlanPricing.isSimplifiedPricing

  const [devModePricing, setDevModePricing] = useState<{
    expression?: IDevModePricingVariables
    calculator?: IDevModePricingVariables
  }>()

  const setDevModePricingVariablesToSubmition = useCallback(
    (pricingVariables: IDevModePricingVariables, type: "expression" | "calculator") => {
      setDevModePricing((prevState) => ({
        ...prevState,
        [type]: pricingVariables
      }))
    },
    []
  )

  // Purchase Plan Summary

  const purchasePlanSummary = {
    planName: purchasePlanForm.name,
    selectedProduct: purchasePlanForm.product,
    isDevMode,
    pricingCalculator: devModePricing?.calculator,
    fromPriceUnit: purchasePlanPricing.fromPriceUnit,
    fromPrice: purchasePlanPricing.fromPrice,
    pricingUnit,
    pricingVariablesList
  }

  // Validations

  const isDevModeValid = devModePricing?.calculator?.isValid && devModePricing?.expression?.isValid

  const isValidMonthlyPricing = useMemo(() => {
    return (
      pricingUnit === PricingUnits.MONTHLY &&
      !isDevMode &&
      !!purchasePlanPricing.fromPrice &&
      +purchasePlanPricing?.fromPrice > 0 &&
      pricingVariablesList.length > 0 &&
      pricingVariablesList.every(({ usageLimit }) => usageLimit && +usageLimit > 0)
    )
  }, [pricingVariablesList, purchasePlanPricing.fromPrice, pricingUnit, isDevMode])

  const isValidPricingVariables =
    (!isDevMode && isValidSimplePricing) || (isDevMode && isDevModeValid === true) || isValidMonthlyPricing

  const isValidPurchasePlan = isValidPurchasePlanForm && isValidPricingVariables

  const overWritePurchasePlanData = useCallback((data: App.IPurchasePlanPayload) => {
    setPurchasePlanForm({
      name: data.name,
      planKey: data.planKey,
      description: data.description,
      product: data.product.id,
      productLabel: data.product.name,
      isEnterprise: data.isEnterprise
    })

    setPurchasePlanPricing({
      fromPrice: data.fromPrice,
      fromPriceUnit: data.fromPriceUnit,
      maxLimit: data.maxLimit,
      isSimplifiedPricing: data.isSimplifiedPricing
    })

    if (data.isSimplifiedPricing && data.pricingVariables) {
      const mappedPricingVariables = data.pricingVariables.map((v) => ({ ...v, isUpdatable: false }))

      replacePricingVariablesList(mappedPricingVariables)
    }

    if (data.isSimplifiedPricing) {
      setPricingUnit(data.pricingUnit ?? PricingUnits.CONSUMPTION)
    }

    replaceBenefitsList(data.benefits.map((benefit: string) => ({ value: benefit })))
  }, [])

  return {
    isValidPurchasePlan,
    purchasePlanSummary,
    overWritePurchasePlanData,
    pricingVariables: {
      pricingVariablesList,
      addPricingVariable,
      removePricingVariable,
      resetList,
      replacePricingVariablesList,
      changePricingVariable,
      handlePricingChange,
      purchasePlanPricing,
      pricingUnit,
      setPricingUnit
    },
    purchasePlanForm: {
      data: purchasePlanForm,
      handlePurchasePlanFormChange,
      isValidPurchasePlanForm: isValidPurchasePlan
    },
    benefits: {
      benefitsList,
      addBenefit,
      changeBenefit,
      removeBenefit,
      disabled: !isValidBenefits || !isValidPurchasePlan
    },
    isDevMode,
    setDevModePricingVariablesToSubmition,
    devModePricing
  }
}

export default usePurchasePlan
